import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Toolbar,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  PlayArrow,
  Message,
  Image as ImageIcon,
  VideoLibrary,
  MusicNote,
  Description,
  CompareArrows,
  Input,
  Menu,
  Label,
  LabelOff,
  Code,
  Http,
  Flag,
  Timer,
  Keyboard,
  Forward,
  Settings,
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import NodeConfigModal from "./Modals/NodeConfigModal";
import api from "../../services/api";
// ✅ Importar nodos personalizados modernos
import StartNode from "./Nodes/StartNode";
import TextNode from "./Nodes/TextNode";
import ImageNode from "./Nodes/ImageNode";
import VideoNode from "./Nodes/VideoNode";
import AudioNode from "./Nodes/AudioNode";
import DocumentNode from "./Nodes/DocumentNode";
import ConditionNode from "./Nodes/ConditionNode";
import InputNode from "./Nodes/InputNode";
import MenuNode from "./Nodes/MenuNode";
import TagNode from "./Nodes/TagNode";
import RemoveTagNode from "./Nodes/RemoveTagNode";
import OpenAINode from "./Nodes/OpenAINode";
import HTTPRequestNode from "./Nodes/HTTPRequestNode";
import WaitNode from "./Nodes/WaitNode";
import TypingNode from "./Nodes/TypingNode";
import SetVariableNode from "./Nodes/SetVariableNode";

const useStyles = makeStyles((theme) => ({
  editorContainer: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 120px)",
    width: "100%",
    backgroundColor: theme.palette.background.default,
  },
  toolbar: {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
  },
  content: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  sidebar: {
    width: 250,
    padding: theme.spacing(1.5),
    borderRight: `1px solid ${theme.palette.divider}`,
    overflowY: "auto",
    backgroundColor: theme.palette.background.paper,
  },
  reactFlowWrapper: {
    flex: 1,
    height: "100%",
    position: "relative",
    backgroundColor: theme.palette.background.default,
  },
  nodeCard: {
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  nodeCardContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: `${theme.spacing(1.25)} ${theme.spacing(1.5)} !important`,
  },
  nodeIcon: {
    fontSize: 20,
  },
  nodeLabel: {
    fontWeight: 500,
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  nodeBadge: {
    display: "none", // ✅ Ocultar badge para diseño minimalista
  },
  categoryTitle: {
    fontWeight: 600,
    fontSize: 12,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    "&:first-of-type": {
      marginTop: 0,
    },
  },
}));

// ✅ Tipos de nodos personalizados modernos
const nodeTypes = {
  start: StartNode,
  text: TextNode,
  message: TextNode, // ✅ Retrocompatibilidad: 'message' usa TextNode
  image: ImageNode,
  video: VideoNode,
  audio: AudioNode,
  document: DocumentNode,
  condition: ConditionNode,
  input: InputNode,
  menu: MenuNode,
  tag: TagNode,
  removeTag: RemoveTagNode,
  openai: OpenAINode,
  httpRequest: HTTPRequestNode,
  wait: WaitNode,
  typing: TypingNode,
  setVariable: SetVariableNode,
};

const FlowEditorReactFlow = ({ flowId, initialFlow, onSave, onCancel }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [flowName, setFlowName] = useState(initialFlow?.name || "");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [saving, setSaving] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [tagsCache, setTagsCache] = useState([]);
  const [promptsCache, setPromptsCache] = useState([]); // ✅ Cache de tags para resolver nombres
  const initialFlowRef = useRef(null);
  const isMountedRef = useRef(true);

  // ✅ Cleanup para prevenir actualizaciones en componente desmontado
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ✅ Cargar tags al iniciar el editor (para resolver nombres de tags en nodos existentes)
  useEffect(() => {
    const loadTags = async () => {
      try {
        const { data } = await api.get("/tags");
        const tagsArray = Array.isArray(data) ? data : (Array.isArray(data?.tags) ? data.tags : []);
        if (isMountedRef.current) {
          setTagsCache(tagsArray);
        }
      } catch (error) {
        console.error("Error cargando tags:", error);
        if (isMountedRef.current) {
          setTagsCache([]);
        }
      }
    };
    loadTags();
  }, []);

  // ✅ Cargar prompts (Agentes IA) al iniciar el editor (para resolver nombres en nodos existentes)
  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const { data } = await api.get("/prompt");
        const promptsArray = Array.isArray(data?.prompts) ? data.prompts : [];
        if (isMountedRef.current) {
          setPromptsCache(promptsArray);
        }
      } catch (error) {
        console.error("Error cargando prompts:", error);
        if (isMountedRef.current) {
          setPromptsCache([]);
        }
      }
    };
    loadPrompts();
  }, []);

  // ✅ Cargar prompts (Agentes IA) al iniciar el editor (para resolver nombres en nodos existentes)
  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const { data } = await api.get("/prompt");
        const promptsArray = Array.isArray(data?.prompts) ? data.prompts : [];
        if (isMountedRef.current) {
          setPromptsCache(promptsArray);
        }
      } catch (error) {
        console.error("Error cargando prompts:", error);
        if (isMountedRef.current) {
          setPromptsCache([]);
        }
      }
    };
    loadPrompts();
  }, []);

  // ✅ Cargar flujo inicial y guardar referencia para detectar cambios
  useEffect(() => {
    if (initialFlow?.flow && isMountedRef.current) {
      try {
        const flowData = typeof initialFlow.flow === "string" 
          ? JSON.parse(initialFlow.flow) 
          : initialFlow.flow;
        
        let reactFlowNodes = [];
        let reactFlowEdges = [];
        
        if (flowData.nodes && isMountedRef.current) {
          // Convertir nuestros nodos al formato de React Flow
          reactFlowNodes = flowData.nodes.map((node) => {
            const nodeData = {
              label: node.label || node.type,
              ...node.data,
            };
            
            // ✅ Si es nodo tag/removeTag y tiene tagId pero no tagName, resolver el nombre
            if ((node.type === 'tag' || node.type === 'removeTag') && nodeData.tagId && !nodeData.tagName) {
              // Buscar el tag en el cache (se cargará en otro useEffect)
              // Si no está disponible aún, se resolverá cuando se actualice tagsCache
            }
            
            return {
              id: node.id,
              type: node.type || "default",
              position: node.position || { x: 0, y: 0 },
              data: nodeData,
            };
          });
          if (isMountedRef.current) {
            setNodes(reactFlowNodes);
          }
        }
        
        if (flowData.edges && isMountedRef.current) {
          // Convertir nuestras conexiones al formato de React Flow
          reactFlowEdges = flowData.edges.map((edge) => ({
            id: edge.id || `edge-${edge.source}-${edge.target}`,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle || null, // ✅ CRÍTICO: Preservar sourceHandle para nodos Condition
            targetHandle: edge.targetHandle || null, // ✅ Preservar targetHandle también
            type: "smoothstep",
            markerEnd: {
              type: MarkerType.ArrowClosed,
            },
          }));
          if (isMountedRef.current) {
            setEdges(reactFlowEdges);
          }
        }
        
        // ✅ Guardar referencia inicial para detectar cambios
        if (isMountedRef.current) {
          initialFlowRef.current = {
            name: initialFlow.name || "",
            nodes: reactFlowNodes,
            edges: reactFlowEdges,
          };
          setHasUnsavedChanges(false);
        }
      } catch (error) {
        console.error("Error parsing flow data:", error);
        if (isMountedRef.current) {
          setNodes([]);
          setEdges([]);
          initialFlowRef.current = {
            name: initialFlow.name || "",
            nodes: [],
            edges: [],
          };
        }
      }
    } else if (!initialFlow && isMountedRef.current) {
      // Flujo nuevo
      initialFlowRef.current = {
        name: "",
        nodes: [],
        edges: [],
      };
      setHasUnsavedChanges(false);
    }
  }, [initialFlow, setNodes, setEdges]);

  // ✅ Resolver nombres de tags cuando se carga el cache de tags
  useEffect(() => {
    if (tagsCache.length === 0) return;
    
    setNodes((currentNodes) => {
      return currentNodes.map((node) => {
        // ✅ Si es nodo tag/removeTag y tiene tagId pero no tagName, resolver el nombre
        if ((node.type === 'tag' || node.type === 'removeTag') && node.data?.tagId && !node.data?.tagName) {
          const tag = tagsCache.find(t => t.id === node.data.tagId);
          if (tag) {
            return {
              ...node,
              data: {
                ...node.data,
                tagName: tag.name,
              },
            };
          }
        }
        return node;
      });
    });
  }, [tagsCache, setNodes]);

  // ✅ Resolver nombres de Agentes IA cuando se carga el cache de prompts
  useEffect(() => {
    if (promptsCache.length === 0) return;
    
    setNodes((currentNodes) => {
      return currentNodes.map((node) => {
        // ✅ Si es nodo openai y tiene promptId pero no promptName, resolver el nombre
        if (node.type === 'openai' && node.data?.promptId && !node.data?.promptName) {
          const prompt = promptsCache.find(p => p.id === node.data.promptId);
          if (prompt) {
            return {
              ...node,
              data: {
                ...node.data,
                promptName: prompt.name,
              },
            };
          }
        }
        return node;
      });
    });
  }, [promptsCache, setNodes]);

  // ✅ Detectar cambios en el flow
  useEffect(() => {
    if (!initialFlowRef.current) return;
    
    const currentFlowStr = JSON.stringify({
      name: flowName,
      nodes: nodes.map(n => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
      edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle })),
    });
    
    const initialFlowStr = JSON.stringify({
      name: initialFlowRef.current.name,
      nodes: initialFlowRef.current.nodes.map(n => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
      edges: initialFlowRef.current.edges.map(e => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle })),
    });
    
    setHasUnsavedChanges(currentFlowStr !== initialFlowStr);
  }, [flowName, nodes, edges]);

  // ✅ Nodos disponibles organizados por categorías (Arquitectura Typebot)
  const nodeCategories = {
    events: {
      title: "Events",
      nodes: [
        {
          type: "start",
          label: "Start",
          icon: <Flag />,
          color: "#9c88ff",
          description: "Nodo inicial del flujo",
        },
      ],
    },
    bubbles: {
      title: "Bubbles",
      nodes: [
        {
          type: "text",
          label: "Texto",
          icon: <Message />,
          color: "#2196F3",
          description: "Enviar mensaje de texto",
        },
        {
          type: "image",
          label: "Imagen",
          icon: <ImageIcon />,
          color: "#4CAF50",
          description: "Enviar imagen con caption",
        },
        {
          type: "video",
          label: "Video",
          icon: <VideoLibrary />,
          color: "#FF5722",
          description: "Enviar video con caption",
        },
        {
          type: "audio",
          label: "Audio",
          icon: <MusicNote />,
          color: "#9C27B0",
          description: "Enviar audio con caption",
        },
        {
          type: "document",
          label: "Documento",
          icon: <Description />,
          color: "#607D8B",
          description: "Enviar documento con caption",
        },
        {
          type: "tag",
          label: "Agregar Tag",
          icon: <Label />,
          color: "#4CAF50",
          description: "Agregar tag al ticket",
        },
        {
          type: "removeTag",
          label: "Remover Tag",
          icon: <LabelOff />,
          color: "#F44336",
          description: "Remover tag del ticket",
        },
      ],
    },
    inputs: {
      title: "Inputs",
      nodes: [
        {
          type: "input",
          label: "Input",
          icon: <Input />,
          color: "#ff6b35",
          description: "Solicitar input del usuario",
        },
        {
          type: "menu",
          label: "Menú",
          icon: <Menu />,
          color: "#ff6b35",
          description: "Mostrar menú con opciones",
        },
      ],
    },
    integrations: {
      title: "Integraciones",
      nodes: [
        {
          type: "openai",
          label: "Agente IA",
          icon: <Code />,
          color: "#9c88ff",
          description: "Usar Agente IA existente",
        },
        {
          type: "httpRequest",
          label: "HTTP Request",
          icon: <Http />,
          color: "#9c88ff",
          description: "Hacer petición HTTP",
        },
      ],
    },
    logic: {
      title: "Logic",
      nodes: [
        {
          type: "condition",
          label: "Condición",
          icon: <CompareArrows />,
          color: "#9c88ff",
          description: "Evaluar condición lógica",
        },
        {
          type: "wait",
          label: "Wait",
          icon: <Timer />,
          color: "#9c88ff",
          description: "Esperar tiempo antes de continuar",
        },
        {
          type: "typing",
          label: "Typing",
          icon: <Keyboard />,
          color: "#9c88ff",
          description: "Simular que el bot está escribiendo",
        },
        {
          type: "setVariable",
          label: "Set variable",
          icon: <Settings />,
          color: "#9c88ff",
          description: "Establecer variable con valor",
        },
      ],
    },
  };

  // ✅ Array plano para compatibilidad (todos los nodos)
  const availableNodes = [
    ...nodeCategories.events.nodes,
    ...nodeCategories.bubbles.nodes,
    ...nodeCategories.inputs.nodes,
    ...nodeCategories.integrations.nodes,
    ...nodeCategories.logic.nodes,
  ];

  // ✅ Agregar nodo al canvas
  const handleAddNode = (nodeType) => {
    const newNode = {
      id: `node-${Date.now()}`,
      type: nodeType,
      position: {
        x: Math.random() * 400 + 100,
        y: Math.random() * 400 + 100,
      },
      data: {
        label: availableNodes.find(n => n.type === nodeType)?.label || nodeType,
      },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  // ✅ Conectar nodos
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => addEdge(params, eds));
    },
    [setEdges]
  );

  // ✅ Abrir modal de configuración
  const handleNodeClick = (event, node) => {
    setSelectedNode(node);
    setConfigModalOpen(true);
  };

  // ✅ Guardar configuración de nodo
  const handleSaveNodeConfig = (updatedNode) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === updatedNode.id ? { ...n, data: updatedNode.data } : n))
    );
    setConfigModalOpen(false);
    setSelectedNode(null);
  };

  // ✅ Guardar flujo (NO cierra el editor)
  const handleSave = async () => {
    if (!flowName.trim()) {
      toast.error(i18n.t("flowbuilders.validation.nameRequired"));
      return;
    }

    try {
      if (!isMountedRef.current) return;
      setSaving(true);
      
      // Convertir nodos de React Flow a nuestro formato
      const flowNodes = nodes.map((node) => ({
        id: node.id,
        type: node.type,
        label: node.data.label,
        position: node.position,
        data: node.data,
      }));

      // Convertir edges de React Flow a nuestro formato
      const flowEdges = edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle || null, // ✅ CRÍTICO: Guardar sourceHandle para nodos Condition
        targetHandle: edge.targetHandle || null, // ✅ Guardar targetHandle también
      }));

      const flowData = {
        nodes: flowNodes,
        edges: flowEdges,
      };

      await onSave({
        id: flowId,
        flow: flowData,
        name: flowName,
      }, false);

      // ✅ Actualizar referencia inicial después de guardar
      initialFlowRef.current = {
        name: flowName,
        nodes: nodes,
        edges: edges,
      };
      setHasUnsavedChanges(false);

      toast.success(i18n.t("flowbuilders.toasts.saveSuccess") || "Flujo guardado exitosamente");
      // ✅ NO cerrar el editor - el usuario permanece trabajando
    } catch (error) {
      toast.error(i18n.t("flowbuilders.toasts.saveError"));
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  // ✅ Manejar cierre con confirmación si hay cambios
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCloseConfirmDialog(true);
    } else {
      onCancel();
    }
  };

  // ✅ Confirmar cierre y descartar cambios
  const handleConfirmClose = () => {
    setShowCloseConfirmDialog(false);
    onCancel();
  };

  return (
    <Box className={classes.editorContainer}>
      {/* ✅ Toolbar - MEJORADO */}
      <Toolbar className={classes.toolbar}>
        <Box display="flex" alignItems="center" gap={2} flex={1}>
          <TextField
            value={flowName}
            onChange={(e) => setFlowName(e.target.value)}
            placeholder={i18n.t("flowbuilders.editor.newFlow") || "Nuevo Flujo"}
            variant="outlined"
            size="small"
            style={{
              minWidth: 300,
              backgroundColor: theme.palette.background.paper,
            }}
            InputProps={{
              style: {
                fontWeight: 600,
                fontSize: 16,
              },
            }}
          />
          {nodes.length > 0 && (
            <Chip
              label={`${nodes.length} nodo${nodes.length > 1 ? "s" : ""}`}
              size="small"
              style={{
                backgroundColor: theme.palette.mode === 'dark' 
                  ? theme.palette.primary.dark 
                  : "#e3f2fd",
                color: theme.palette.mode === 'dark' 
                  ? theme.palette.primary.light 
                  : "#1976d2",
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {hasUnsavedChanges && (
            <Chip
              label="Cambios sin guardar"
              size="small"
              style={{
                backgroundColor: "#ff9800",
                color: "#fff",
              }}
              style={{ marginRight: 8 }}
            />
          )}
          <Tooltip title={i18n.t("flowbuilders.editor.save") || "Guardar"}>
            <IconButton
              color="primary"
              onClick={handleSave}
              disabled={saving}
              style={{
                backgroundColor: theme.palette.primary.main,
                color: "white",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              <SaveIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n.t("flowbuilders.editor.cancel") || "Cerrar"}>
            <IconButton
              onClick={handleCancel}
              style={{
                color: theme.palette.text.secondary,
              }}
            >
              <CancelIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>

      {/* ✅ Contenido */}
      <Box className={classes.content}>
        {/* ✅ Sidebar minimalista estilo Typebot */}
        <Paper 
          className={classes.sidebar} 
          elevation={0}
        >
          {/* ✅ Renderizar nodos por categorías - Estilo Typebot minimalista */}
          {Object.entries(nodeCategories).map(([categoryKey, category]) => (
            <Box key={categoryKey}>
              <Typography className={classes.categoryTitle}>
                {category.title}
              </Typography>
              {category.nodes.map((node) => (
                <Card
                  key={node.type}
                  className={classes.nodeCard}
                  onClick={() => handleAddNode(node.type)}
                  elevation={0}
                >
                  <CardContent className={classes.nodeCardContent}>
                    <Box
                      style={{
                        color: categoryKey === 'bubbles' 
                          ? (theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.main)
                          : categoryKey === 'inputs' 
                          ? '#ff6b35' 
                          : '#9c88ff',
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {node.icon}
                    </Box>
                    <Typography className={classes.nodeLabel}>
                      {node.label}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ))}
        </Paper>

        {/* ✅ Canvas con React Flow */}
        <Box className={classes.reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={handleNodeClick}
            nodeTypes={nodeTypes}
            fitView
            deleteKeyCode={["Backspace", "Delete"]}
            defaultEdgeOptions={{
              style: { 
                strokeWidth: 3, 
                stroke: theme.palette.mode === 'dark' ? '#757575' : '#b1b1b7' 
              },
              animated: true,
              type: "smoothstep",
            }}
            connectionLineStyle={{ 
              strokeWidth: 3, 
              stroke: theme.palette.mode === 'dark' ? '#757575' : '#b1b1b7' 
            }}
            snapToGrid={true}
            snapGrid={[20, 20]}
          >
            <Background
              variant="dots"
              gap={20}
              size={1.5}
              color={theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0'}
              style={{ backgroundColor: theme.palette.background.default }}
            />
            <Controls
              style={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
                boxShadow: theme.shadows[2],
              }}
            />
            <MiniMap
              nodeColor={(node) => {
                if (node.type === "start") return "#667eea";
                if (node.type === "message") return "#2196F3";
                if (node.type === "condition") return "#FF9800";
                return theme.palette.mode === 'dark' ? '#757575' : '#b1b1b7';
              }}
              maskColor={theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)'}
              style={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
              }}
              nodeBorderRadius={8}
            />
          </ReactFlow>
        </Box>
      </Box>

      {/* ✅ Modal de Configuración de Nodo */}
      <NodeConfigModal
        open={configModalOpen}
        onClose={() => {
          setConfigModalOpen(false);
          setSelectedNode(null);
        }}
        node={selectedNode}
        onSave={handleSaveNodeConfig}
      />

      {/* ✅ Diálogo de confirmación al cerrar con cambios sin guardar */}
      <Dialog
        open={showCloseConfirmDialog}
        onClose={() => setShowCloseConfirmDialog(false)}
      >
        <DialogTitle>¿Descartar cambios?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tienes cambios sin guardar. ¿Estás seguro de que deseas cerrar el editor sin guardar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloseConfirmDialog(false)} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleConfirmClose} color="secondary" variant="contained">
            Descartar cambios
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlowEditorReactFlow;
